import SaveOutlinedIcon from "@mui/icons-material/SaveOutlined";
import { LoadingButton } from "@mui/lab";
import {
  Checkbox,
  FormControlLabel,
  Grid,
  TextField,
  Typography,
} from "@mui/material";
import { FormikProps, useFormik } from "formik";
import React, { useEffect, useState } from "react";
import * as yup from "yup";
import { useAppDispatch, useAppSelector } from "../../../app/hooks";
import { selectConstants } from "../../../common/dropdowns/Api/constantSlice";
import ApplicationStatus, {
  BackendApplicationStage,
} from "../../../common/dropdowns/ApplicationStatus";
import DeclineReasons from "../../../common/dropdowns/DeclineReasons";
import Universities from "../../../common/dropdowns/Universities";
import MessageResponse from "../../../common/feedback/messageResponse";
import { GetMessageReponse } from "../../../common/Logic/ServerResponse";
import {
  FeedbackDto,
  UpdateApplicationStatusRequestDto,
} from "../../../common/models/requests/FeedbackDto";
import { UpdateApplicationStatusDto } from "../models";
import { selectAdmin, updateApplicants } from "../adminSlice";
import { UpdateApplicationStatusThunk } from "../thunks";
import { APPLICATION_STAGE } from "../../../common/models/enums/applicationStage";

interface Props {
  applicationId: number;
  currentApplicationStatus: BackendApplicationStage;
  setFilterd: React.Dispatch<React.SetStateAction<APPLICATION_STAGE>>;
  applicantName: string;
}
export default function SetStatus({
  applicationId,
  currentApplicationStatus,
  setFilterd,
  applicantName,
}: Props) {
  const dispatch = useAppDispatch();
  const selData = useAppSelector(selectConstants);
  const admin = useAppSelector(selectAdmin);
  const [feedback, setFeedback] = useState<FeedbackDto>({} as FeedbackDto);
  const [sendEmailCheckboxToDisable, setSendEmailCheckboxToDisable] =
    useState(false);

  const validationSchema = yup.object({
    applicationStages: yup
      .mixed()
      .oneOf(Object.values(APPLICATION_STAGE), "Invalid application status")
      .required("Please select application status"),
    institutionId: yup.number().when("applicationStages", {
      is: 13,
      then: (schema) =>
        schema
          .required("Please select Institution")
          .min(1, "Please select Institution"),
    }),
    declineReasonId: yup.array().when("applicationStages", {
      is: 10,
      then: (schema) =>
        schema
          .required("Please select at least one decline reason")
          .min(1, "Please select at least one decline reason"),
    }),
    comment: yup.string().when("applicationStages", {
      is:
        APPLICATION_STAGE.AddressDetails ||
        APPLICATION_STAGE.DocumentsUpload ||
        APPLICATION_STAGE.LegalGuardianDetails ||
        APPLICATION_STAGE.InstitutionDetails ||
        APPLICATION_STAGE.StudyDetails ||
        APPLICATION_STAGE.PersonalDetails,
      then: (schema) =>
        schema
          .required("Please enter comments")
          .min(10, "Please enter at least 10 characters"),
    }),
  });

  const formik: FormikProps<UpdateApplicationStatusRequestDto> =
    useFormik<UpdateApplicationStatusRequestDto>({
      initialValues: {
        applicationStages: -1,
        institutionId: -1,
        declineReasonId: [],
        comment: "",
        sendEmail: true,
      },
      validationSchema: validationSchema,
      onSubmit: (values: UpdateApplicationStatusRequestDto) => {
        const payload = {
          applicationId: applicationId,
          applicationStage: values.applicationStages,
          universityChoiceId: values.institutionId,
          declineReasonId: values.declineReasonId,
          comment: values.comment,
          sendEmail: values.sendEmail,
        } as UpdateApplicationStatusDto;

        dispatch(UpdateApplicationStatusThunk(payload)).then((resp: any) => {
          if (
            admin.applicationDatesState.status === "failed" ||
            resp.payload?.errors
          ) {
            const feed = GetMessageReponse(
              admin.applicationDatesState.error,
              false,
              resp.type
            );
            setFeedback(feed);
            return;
          }
          if (
            (resp.type.includes("fulfilled") &&
              resp.payload?.validation?.code === 201) ||
            (resp.type.includes("fulfilled") &&
              resp.payload?.validation?.code === 200)
          ) {
            formik.resetForm();
            const feed = GetMessageReponse(resp.payload, true, resp.type);

            setFeedback(feed);
            dispatch(
              updateApplicants({
                ...payload,
                applicationStatus: resp.payload?.data,
              } as UpdateApplicationStatusDto)
            );

            setTimeout(() => {
              setFilterd(APPLICATION_STAGE.All);
            }, 2000);
          }
        });
      },
    });

  useEffect(() => {
    if (
      formik.values.applicationStages ===
      APPLICATION_STAGE.ApplicationSentForFurtherReview
    ) {
      formik.setFieldValue("sendEmail", false);
      setSendEmailCheckboxToDisable(true);
    } else {
      formik.setFieldValue("sendEmail", true);
      setSendEmailCheckboxToDisable(false);
    }
  }, [formik.values.applicationStages]);

  return (
    <>
      <Typography
        variant="body2"
        component="h2"
        mb={3}
        sx={{ borderLeft: "3px solid #85B825", paddingLeft: 1 }}
      >
        Current Status:{" "}
        {String(currentApplicationStatus)
          .replace(/([A-Z])/g, " $1")
          .trim()}
      </Typography>

      <MessageResponse feedback={feedback} />
      {selData.constantState.applicationStages && (
        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <ApplicationStatus
              formik={formik}
              isDisabled={false}
              currentApplicationStatus={currentApplicationStatus}
            />
          </Grid>

          {formik.values.applicationStages ===
            APPLICATION_STAGE.ApplicationSuccessful && (
            <Grid item xs={12} md={6}>
              <Universities formik={formik} />
            </Grid>
          )}

          {formik.values.applicationStages ===
            APPLICATION_STAGE.ApplicationDeclined && (
            <Grid item xs={12} md={6}>
              <DeclineReasons formik={formik} isDisabled={false} />
            </Grid>
          )}

          {(formik.values.applicationStages ===
            APPLICATION_STAGE.PersonalDetails ||
            formik.values.applicationStages ===
              APPLICATION_STAGE.AddressDetails ||
            formik.values.applicationStages ===
              APPLICATION_STAGE.DocumentsUpload ||
            formik.values.applicationStages ===
              APPLICATION_STAGE.StudyDetails ||
            formik.values.applicationStages ===
              APPLICATION_STAGE.InstitutionDetails ||
            formik.values.applicationStages ===
              APPLICATION_STAGE.LegalGuardianDetails) && (
            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                id="comment"
                name="comment"
                minRows={4}
                label="Enter comments"
                value={formik.values.comment}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                type="text"
                error={formik.touched.comment && Boolean(formik.errors.comment)}
                helperText={formik.touched.comment && formik.errors.comment}
              />
            </Grid>
          )}

          <Grid item xs={12}>
            <FormControlLabel
              control={
                <Checkbox
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  name="sendEmail"
                  checked={formik.values.sendEmail}
                  value={formik.values.sendEmail}
                  disabled={sendEmailCheckboxToDisable}
                />
              }
              label={
                formik.values.sendEmail
                  ? `${applicantName} will be notified via Email for this status update`
                  : `${applicantName} will NOT be notified for this status update`
              }
            />
          </Grid>

          <Grid item xs={12}>
            <LoadingButton
              sx={{ float: "left", marginBottom: 1.5 }}
              color="primary"
              variant="contained"
              startIcon={<SaveOutlinedIcon />}
              onClick={() => {
                formik.submitForm();
              }}
              disabled={!formik.dirty}
              loading={admin.applicationDatesState.status === "loading"}
              loadingPosition="start"
            >
              Update Status
            </LoadingButton>
          </Grid>
        </Grid>
      )}
    </>
  );
}
